import { Suspense, useCallback, useEffect } from "react";
import { useLocation, useNavigate, useRoutes } from "react-router-dom";
import { getIndexPath as getKPIOverviewPath } from "~/features/analysis/kpi-overview/routes";
import { getLoginPath } from "~/features/auth/routes";
import { getIndexPath as getProVersionPagePath } from "~/features/buy-pro-version/routes";
import { LoadingText } from "~/features/common/components/LoadingText";
import { useAuth } from "~/features/common/context/AuthContext";
import { getIndexPath as getPartsPath } from "~/features/parts/routes";
import { getIndexPath as getSimulationPath } from "~/features/simulation/routes";
import {
  getTimeSheetExportPath,
  getTimeTrackerAdminPath,
  getIndexPath as getTimeTrackerPath,
} from "~/features/timetracker/routes";
import routes from "~react-pages";
import { navigationShortcutList } from "./features/common/components/Sidebar/components/SidebarNavigationLinks";
import {
  EnumNavStatus,
  restrictRoutesAndFeatures,
} from "./features/common/security/routeRestriction/ResrictRoutesAndFeatures";
import { isTest } from "./features/common/utils/IsTest";
import { getRole } from "./features/common/utils/loginChecks";
import { showErrorNotification } from "./features/common/utils/services/notificationService";
import { EnumPersonRole } from "./generated/graphql";
import { routeIsAllowed } from "./graphql/fetcher";
import { getIndexPath } from "./features/home/routes";

const disallowedRoutes = [
  getKPIOverviewPath({}),
  getSimulationPath({}),
  getTimeTrackerPath({}),
  getPartsPath({}),
  getTimeSheetExportPath({}),
  getTimeTrackerAdminPath({}),
];

export const RenderRoutes = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const myRole = getRole();

  const handleKeyPress = useCallback((event: { shiftKey: boolean; ctrlKey: boolean; key: string | number }) => {
    if (event.shiftKey && event.ctrlKey) {
      const returnPathFunction = navigationShortcutList[event.key];
      if (returnPathFunction) {
        navigate(returnPathFunction({}));
      }
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyPress);
    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [handleKeyPress]);

  useEffect(() => {
    // we check that the user is logged in because when a user logs out the notification is sent because of how we handle logout, and we don't want it in this case
    if (auth.isLoggedIn) {
      const navStatus = restrictRoutesAndFeatures(location, myRole);

      // if user is an admin and route isn't found or for some reason it's restricted by the route lookup then don't restrict navigation
      if (myRole === EnumPersonRole.ADMIN) {
        if (navStatus.status === EnumNavStatus.NOT_FOUND) {
          showErrorNotification("This route doesn't exist");
          navigate(getIndexPath({}));
          return;
        }
        if (navStatus.status === EnumNavStatus.DENIED) {
          return;
        }
      } else {
        // if the user tries to access a parent-level link try and redirect them to a child route that is allowed, with a warning toast
        if (navStatus.status === EnumNavStatus.FALLBACK_REDIRECT) {
          navigate(navStatus?.redirectURL);
          showErrorNotification(
            "Your role restricts you from accessing this feature. If you think that this is in error please contact your administrator",
          );
          return;
          // if no fallback link warn the user that they are in a restricted place that their role doesn't allow access to and navigate backwards
        } else if (navStatus.status === EnumNavStatus.DENIED) {
          navigate(getIndexPath({}));
          showErrorNotification(
            `Your role restricts you from accessing this feature. If you think that this is in error please contact your administrator`,
          );
          return;
          // if route not found then tell user and navigate back
        } else if (navStatus.status === EnumNavStatus.NOT_FOUND) {
          navigate(getIndexPath({}));
          showErrorNotification("This route doesn't exist");
          return;
        }
      }
    }

    if (!auth.isLoggedIn && !routeIsAllowed(location.pathname)) {
      navigate(getLoginPath({}));
    }
  }, [auth.isLoggedIn, location.pathname]);

  // Added an 'isTest' check here so that we can render all of the features during testing
  useEffect(() => {
    if (!auth.isProUser && !auth.isLoading && !isTest) {
      if (disallowedRoutes.includes(location.pathname)) {
        navigate(getProVersionPagePath({}));
      }
    }
  }, [location.pathname, auth.isProUser, auth.isLoading]);

  const generatedRoutes = useRoutes(routes);
  return <Suspense fallback={<LoadingText classNames="text-2xl" />}>{generatedRoutes}</Suspense>;
};
